import { z } from "zod";
// Vision service contract from pkanogram.md. These are server-side inputs,
// separate from the editor's canonical left-to-right arrangement.
const units = z.number().int().positive().max(1000);
export const visionBox = z
    .tuple([
    z.number().nonnegative(),
    z.number().nonnegative(),
    z.number().nonnegative(),
    z.number().nonnegative(),
])
    .refine(([x1, y1, x2, y2]) => x2 > x1 && y2 > y1, "Invalid image box");
const identity = {
    sku_id: z.string().max(500).nullable(),
    product_name: z.string().max(500).nullable(),
};
const group = z.object({
    kind: z.literal("product_group"),
    ...identity,
    bbox: visionBox,
    h_facings: units,
    v_facings: units.nullable(),
    v_facings_by_column: z.array(units).min(1).max(1000),
});
const review = z.object({
    kind: z.literal("review_column"),
    bbox: visionBox,
    reason: z.enum(["mixed_stack", "unresolved_stack"]),
    detections: z
        .array(z.object({
        detection_id: z.number().int().nonnegative(),
        ...identity,
        bbox: visionBox,
    }))
        .min(1)
        .max(1000),
});
export const visionWarning = z.object({
    stage: z.string().max(100),
    message: z.string().max(1000),
});
export const visionPlanogramResult = z
    .object({
    schema_version: z.literal("1.0"),
    image_id: z.string(),
    llm_validation: z.boolean(),
    rack_roi: visionBox,
    shelf_order: z.literal("top_to_bottom"),
    product_order: z.literal("right_to_left"),
    needs_review: z.boolean(),
    shelves: z
        .array(z.object({
        shelf_number: z.number().int().positive().max(50),
        bbox: visionBox,
        products: z.array(z.discriminatedUnion("kind", [group, review])).max(500),
    }))
        .max(50),
    warnings: z.array(visionWarning).max(100),
})
    .superRefine((result, ctx) => {
    result.shelves.forEach((shelf, i) => {
        if (shelf.shelf_number !== i + 1)
            ctx.addIssue({ code: "custom", message: "Shelves must be consecutive, top to bottom" });
        for (const p of shelf.products) {
            if (p.kind !== "product_group")
                continue;
            const heights = p.v_facings_by_column;
            const common = heights.every((h) => h === heights[0]) ? heights[0] : null;
            if (heights.length !== p.h_facings ||
                common !== p.v_facings ||
                heights.reduce((n, h) => n + h, 0) > 1000)
                ctx.addIssue({ code: "custom", message: "Inconsistent product group counts" });
        }
    });
});
export const visionPlanogramSubmission = z.object({
    job_id: z.string().uuid(),
    status: z.literal("queued"),
    created_at: z.string().datetime({ offset: true }),
    status_url: z.string().min(1),
    result_url: z.string().min(1),
});
export const visionPlanogramJobStatus = z.object({
    job_id: z.string().uuid(),
    operation: z.literal("planogram"),
    queue: z.literal("background"),
    status: z.enum(["queued", "processing", "completed", "failed"]),
});
//# sourceMappingURL=vision-planogram.schemas.js.map